import React, { useState, useEffect, useRef, useCallback } from 'react';
import { movimentacaoEstoqueService, lojasService } from '../../services/api';
import DraggableWindow from './DraggableWindow';

// Operador gravado em scemov_transferencia.CDOPE — sempre 13 (regra do negócio).
const OPERADOR_FIXO = 13;

// Taxonomia de scemov_transferencia.TIPO (espelha CarregaPedidosWorker._query_transf_*
// do estwin). Só 'C' é criado/alterado por esta tela; 'R' vem da rotina automática do
// estwin e 'P' do fluxo de pré-vencidos — ambos entram apenas para visualização.
const TIPO_LABEL = {
  C: 'Transferência do Depto de Compras',
  R: 'Transferência por remanejamento',
  P: 'Transferência de pré-vencidos',
};
const TIPO_SIGLA = { C: 'COMPRAS', R: 'REMANEJ', P: 'PRÉ-VENC' };
const TIPO_ESTILO = (t) => (
  t === 'C' ? { background: '#dbeafe', color: '#1e40af' }
    : t === 'R' ? { background: '#ede9fe', color: '#5b21b6' }
      : t === 'P' ? { background: '#fef3c7', color: '#92400e' }
        : { background: '#f1f5f9', color: '#475569' }
);
// aparência dos botões travados em linhas não editáveis
const BTN_OFF = { borderColor: '#cbd5e1', color: '#94a3b8' };

// A tabela de pendentes tem altura fixa de 7 linhas, cheia ou vazia, para a
// moldura não pular ao trocar de filtro nem ao ficar sem resultado.
const LINHAS_VISIVEIS = 7;
const LINHA_H = 27;
const CABECALHO_H = 30;
const ALTURA_TABELA = CABECALHO_H + LINHAS_VISIVEIS * LINHA_H;

const WindowTransferenciaMercadoria = ({ onClose, onMinimize, isMinimized, zIndex, onFocus, userData }) => {
  const [lojas, setLojas] = useState([]);
  const [origem, setOrigem] = useState(userData?.loja_id || 22);
  const [destino, setDestino] = useState('');
  const [codigo, setCodigo] = useState('');
  const [qtd, setQtd] = useState('');
  // scemov_transferencia não tem coluna LOTE — o campo existia na UI, era enviado
  // e o backend descartava. Removido para não sugerir um controle que não existe.
  const [itens, setItens] = useState([]);            // [{cdprodu, nome, qtd, estoq}]
  const [buscando, setBuscando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const codigoRef = useRef(null);

  // Sub-abas: 'nova' (lançar) | 'cancelar' (cancelar pendentes)
  const [aba, setAba] = useState('nova');
  const [pendentes, setPendentes] = useState([]);
  // filtro da aba pendente: 'TODOS' (padrão) | 'C' Depto Compras | 'R' | 'P'
  const [filtroTipo, setFiltroTipo] = useState('TODOS');
  const [carregandoPend, setCarregandoPend] = useState(false);
  const [cancelandoKey, setCancelandoKey] = useState(null);
  // Quando preenchido, a aba "Lançar" adiciona itens a um DOC já existente
  const [addDoc, setAddDoc] = useState(null); // { doc, destino, destinoNome }

  // inicia o modo "adicionar itens a um DOC existente" (a partir da grid pendente)
  const iniciarAddDoc = (p) => {
    setOrigem(p.FIL);
    setDestino(String(p.DESTINO));
    setAddDoc({ doc: p.NRDOC, destino: String(p.DESTINO), destinoNome: p.DESTINO_NOME });
    setItens([]); setErro(''); setSucesso('');
    setAba('nova');
  };
  const sairAddDoc = () => { setAddDoc(null); setItens([]); setDestino(''); };

  useEffect(() => {
    lojasService.getLojas()
      .then((l) => setLojas((l || []).sort((a, b) => a.numero - b.numero)))
      .catch(() => {});
  }, []);

  // Guarda contra resposta fora de ordem: trocar o filtro rápido dispara várias
  // buscas e a mais lenta pode chegar por último. Só a requisição mais recente escreve.
  const reqPendentesRef = useRef(0);

  const carregarPendentes = useCallback(async () => {
    const reqId = ++reqPendentesRef.current;
    setCarregandoPend(true);
    setErro(''); setSucesso('');
    try {
      const rows = await movimentacaoEstoqueService.listarPendentesOrigem(origem, filtroTipo);
      if (reqId !== reqPendentesRef.current) return;   // chegou tarde, descarta
      setPendentes(rows || []);
    } catch (e) {
      if (reqId !== reqPendentesRef.current) return;
      setErro(e.message || 'Erro ao carregar transferências pendentes.');
    } finally {
      if (reqId === reqPendentesRef.current) setCarregandoPend(false);
    }
  }, [origem, filtroTipo]);

  // ao abrir a aba Cancelar (ou trocar origem nela), recarrega a lista
  useEffect(() => {
    if (aba === 'cancelar') carregarPendentes();
  }, [aba, carregarPendentes]);

  // escopo: 'doc' (documento inteiro) | 'item' (só a linha p)
  const cancelar = async (p, escopo) => {
    const doc = p.NRDOC;
    const porItem = escopo === 'item';
    const key = porItem ? `${doc}-${p.COD}` : `doc-${doc}`;
    const msg = porItem
      ? `Cancelar o item ${p.COD} - ${p.PRODUTO}\ndo documento ${doc}?\n\nRemove esta linha do pedido pendente.`
      : `Cancelar o documento ${doc} INTEIRO?\n\nRemove todos os itens do pedido pendente.`;
    if (!window.confirm(msg)) return;

    setCancelandoKey(key);
    setErro(''); setSucesso('');
    try {
      const r = await movimentacaoEstoqueService.cancelarTransferencia({
        cdfil: parseInt(origem, 10),
        doc,
        tipo: p.TIPO,
        ...(porItem ? { cdprodu: p.COD, cdfildest: p.DESTINO } : {}),
      });
      if (porItem) {
        setSucesso(`Item ${p.COD} do DOC ${doc} cancelado.`);
        setPendentes((prev) => prev.filter((x) => !(String(x.NRDOC) === String(doc) && String(x.COD) === String(p.COD))));
      } else {
        setSucesso(`Documento ${doc} cancelado (${r.itensCancelados} item(ns)).`);
        setPendentes((prev) => prev.filter((x) => String(x.NRDOC) !== String(doc)));
      }
    } catch (e) {
      setErro(e.message || 'Erro ao cancelar.');
    } finally {
      setCancelandoKey(null);
    }
  };

  const nomeLoja = (num) => {
    const l = lojas.find((x) => parseInt(x.numero, 10) === parseInt(num, 10));
    return l ? `${l.numero} - ${l.nome}` : String(num);
  };

  const adicionarItem = useCallback(async () => {
    setErro(''); setSucesso('');
    const cod = String(codigo).trim();
    const q = Number(qtd);
    if (!cod) { setErro('Informe o código do produto.'); return; }
    if (!Number.isFinite(q) || q <= 0) { setErro('Informe uma quantidade maior que zero.'); return; }

    setBuscando(true);
    try {
      const p = await movimentacaoEstoqueService.buscarSaldo(cod, origem);
      if (!p) { setErro('Produto não encontrado na filial de origem.'); return; }
      if (q > Number(p.ESTOQ)) {
        if (!window.confirm(`Saldo na origem é ${p.ESTOQ}. Transferir ${q} mesmo assim?`)) return;
      }
      setItens((prev) => {
        // se já existe o produto, soma a quantidade
        const idx = prev.findIndex((i) => String(i.cdprodu) === String(p.CDPRODU));
        if (idx >= 0) {
          const cp = [...prev];
          cp[idx] = { ...cp[idx], qtd: Number(cp[idx].qtd) + q };
          return cp;
        }
        return [...prev, { cdprodu: p.CDPRODU, nome: p.NOME, qtd: q, estoq: p.ESTOQ }];
      });
      setCodigo(''); setQtd('');
      codigoRef.current?.focus();
    } catch (e) {
      setErro(e.message || 'Erro ao buscar produto.');
    } finally {
      setBuscando(false);
    }
  }, [codigo, qtd, origem]);

  const removerItem = (i) => setItens((prev) => prev.filter((_, idx) => idx !== i));

  const confirmarTransferencia = async () => {
    setErro(''); setSucesso('');
    if (!destino) { setErro('Selecione a filial de destino.'); return; }
    if (parseInt(destino, 10) === parseInt(origem, 10)) { setErro('Origem e destino não podem ser iguais.'); return; }
    if (itens.length === 0) { setErro('Adicione ao menos um item.'); return; }

    const totalUn = itens.reduce((s, i) => s + Number(i.qtd), 0);
    const cab = addDoc
      ? `Adicionar ao DOC ${addDoc.doc}?\n\nOrigem: ${nomeLoja(origem)}\nDestino: ${nomeLoja(destino)}\n`
      : `Confirmar transferência?\n\nOrigem: ${nomeLoja(origem)}\nDestino: ${nomeLoja(destino)}\n`;
    if (!window.confirm(
      cab + `${itens.length} item(ns) · ${totalUn} unidades\n\n` +
      `Lança no pedido pendente do Depto de Compras (TIPO='C'). Não baixa estoque nem coloca em trânsito.`
    )) return;

    // Operador fixo nas transferências/movimentações (regra do negócio): sempre 13
    const operador = OPERADOR_FIXO;

    setSalvando(true);
    try {
      const r = await movimentacaoEstoqueService.criarTransferencia({
        cdfil: parseInt(origem, 10),
        cdfildest: parseInt(destino, 10),
        itens: itens.map((i) => ({ cdprodu: i.cdprodu, qtd: i.qtd })),
        cdope: operador,
        cdfun: operador,
        doc: addDoc ? addDoc.doc : null,
      });
      const somados = (r.detalhes || []).filter((d) => d.acao === 'somado').length;
      setSucesso(`${addDoc ? `DOC ${addDoc.doc} atualizado` : `Transferência criada · DOC ${r.doc}`}`
        + ` · ${r.itens} item(ns)${somados ? ` (${somados} somado(s) a item existente)` : ''}.`);
      setItens([]);
      if (!addDoc) setDestino('');
    } catch (e) {
      setErro(e.message || 'Erro ao criar transferência.');
    } finally {
      setSalvando(false);
    }
  };

  const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 };
  const inp = { width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
  const row = { display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' };
  // estilos da tabela de pendentes
  const thBase = { height: CABECALHO_H, boxSizing: 'border-box', padding: '7px 10px', textAlign: 'left', fontSize: 11.5, fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' };
  const thR = { ...thBase, textAlign: 'right' };
  const tdBase = { height: LINHA_H, boxSizing: 'border-box', padding: '5px 10px', fontSize: 12.5, color: '#1e293b', whiteSpace: 'nowrap' };
  const tdR = { ...tdBase, textAlign: 'right' };
  const tdEll = { ...tdBase, overflow: 'hidden', textOverflow: 'ellipsis' };

  return (
    <DraggableWindow
      title="Transferência de Mercadoria"
      icon="fa-right-left"
      onClose={onClose}
      onMinimize={onMinimize}
      isMinimized={isMinimized}
      zIndex={zIndex}
      onFocus={onFocus}
      initialWidth={880}
    >
      <div style={{ padding: 16 }}>
        {/* Sub-abas */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid #e2e8f0' }}>
          {[['nova', 'Lançar Nova Movimentação'], ['cancelar', 'Movimentação Pendente']].map(([k, t]) => (
            <button key={k} onClick={() => { setAba(k); setErro(''); setSucesso(''); }}
              style={{
                padding: '8px 16px', border: 'none', background: 'transparent', cursor: 'pointer',
                fontWeight: 600, fontSize: 14,
                color: aba === k ? '#0078d7' : '#64748b',
                borderBottom: aba === k ? '2px solid #0078d7' : '2px solid transparent'
              }}>
              {t}
            </button>
          ))}
        </div>

        {/* Filial de origem (compartilhada pelas duas abas) */}
        <div style={row}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={lbl}>{aba === 'nova' ? 'Filial de Origem' : 'Mostrar Transferências da Filial'}</label>
            <select style={inp} value={origem} disabled={!!addDoc && aba === 'nova'}
              onChange={(e) => { setOrigem(e.target.value); setItens([]); }}>
              {lojas.length === 0 && <option value={origem}>{origem}</option>}
              {lojas.map((l) => <option key={l.numero} value={l.numero}>{l.numero} - {l.nome}</option>)}
            </select>
          </div>
          {aba === 'nova' && (
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={lbl}>Filial de Destino</label>
            <select style={inp} value={destino} disabled={!!addDoc}
              onChange={(e) => setDestino(e.target.value)}>
              <option value="">Selecione...</option>
              {lojas.filter((l) => parseInt(l.numero, 10) !== parseInt(origem, 10))
                .map((l) => <option key={l.numero} value={l.numero}>{l.numero} - {l.nome}</option>)}
            </select>
          </div>
          )}
        </div>

        {/* ===================== ABA: NOVA TRANSFERÊNCIA ===================== */}
        {aba === 'nova' && (<>
        {addDoc && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#1e40af' }}>
            <span><i className="fas fa-circle-plus" /> Adicionando itens ao <b>DOC {addDoc.doc}</b> (destino {addDoc.destino} - {addDoc.destinoNome}).</span>
            <button onClick={sairAddDoc} style={{ border: 'none', background: 'transparent', color: '#1e40af', cursor: 'pointer', fontWeight: 600 }}>Sair</button>
          </div>
        )}
        {/* Adicionar item */}
        <div style={{ ...row, alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label style={lbl}>Produto (código / barras)</label>
            <input
              ref={codigoRef}
              style={inp}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); adicionarItem(); } }}
              placeholder="Código e Enter"
              autoFocus
            />
          </div>
          <div style={{ width: 110 }}>
            <label style={lbl}>Qtd</label>
            <input style={inp} type="number" min="0" value={qtd}
              onChange={(e) => setQtd(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); adicionarItem(); } }} />
          </div>
          <button onClick={adicionarItem} disabled={buscando}
            style={{ padding: '9px 16px', border: 'none', borderRadius: 6, background: '#0078d7', color: '#fff', cursor: 'pointer', height: 38 }}>
            {buscando ? '...' : '+ Add'}
          </button>
        </div>

        {/* Grid de itens */}
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ display: 'flex', background: '#f8fafc', fontSize: 12, fontWeight: 700, color: '#64748b', padding: '8px 10px' }}>
            <span style={{ width: 70 }}>Cód.</span>
            <span style={{ flex: 1 }}>Produto</span>
            <span style={{ width: 70, textAlign: 'right' }}>Qtd</span>
            <span style={{ width: 70, textAlign: 'right' }}>Saldo</span>
            <span style={{ width: 36 }} />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {itens.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Nenhum item adicionado.</div>
            ) : itens.map((it, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '7px 10px', borderTop: '1px solid #f1f5f9', fontSize: 13 }}>
                <span style={{ width: 70 }}>{it.cdprodu}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.nome}</span>
                <span style={{ width: 70, textAlign: 'right', fontWeight: 600 }}>{it.qtd}</span>
                <span style={{ width: 70, textAlign: 'right', color: Number(it.qtd) > Number(it.estoq) ? '#dc2626' : '#475569' }}>{it.estoq}</span>
                <span style={{ width: 36, textAlign: 'right' }}>
                  <button onClick={() => removerItem(i)} title="Remover"
                    style={{ border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer' }}>
                    <i className="fas fa-trash" />
                  </button>
                </span>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={confirmarTransferencia}
          disabled={salvando || itens.length === 0 || !destino}
          style={{
            width: '100%', padding: '12px', border: 'none', borderRadius: 8,
            background: (!salvando && itens.length && destino) ? '#16a34a' : '#94a3b8',
            color: '#fff', fontWeight: 700, fontSize: 15,
            cursor: (!salvando && itens.length && destino) ? 'pointer' : 'not-allowed'
          }}>
          {salvando ? 'Enviando...' : 'Confirmar Transferência'}
        </button>
        </>)}

        {/* ===================== ABA: CANCELAR ===================== */}
        {aba === 'cancelar' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: '#64748b' }}>
                Transferências pendentes (não recebidas) da filial {origem}
              </span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}
                  style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', fontSize: 13, cursor: 'pointer' }}>
                  <option value="TODOS">Todos os tipos</option>
                  <option value="C">Depto de Compras</option>
                  <option value="R">Remanejamento</option>
                  <option value="P">Pré-vencidos</option>
                </select>
                <button onClick={carregarPendentes} disabled={carregandoPend}
                  style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: carregandoPend ? 'default' : 'pointer', fontSize: 13, minWidth: 118 }}>
                  <i className={`fas fa-rotate${carregandoPend ? ' fa-spin' : ''}`} /> {carregandoPend ? 'Atualizando...' : 'Atualizar'}
                </button>
              </div>
            </div>
            {/* Altura fixa de 7 linhas + cabeçalho: a moldura não muda de tamanho
                nem com a lista vazia, nem durante a troca de filtro. */}
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, height: ALTURA_TABELA, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', transition: 'opacity 120ms ease', opacity: carregandoPend ? 0.55 : 1 }}>
                <thead>
                  <tr>
                    <th style={thBase}>FIL</th>
                    <th style={thBase}>TIPO</th>
                    <th style={thBase}>COD</th>
                    <th style={thBase}>PRODUTO</th>
                    <th style={thR}>QTD</th>
                    <th style={thBase}>DATA</th>
                    <th style={thBase}>REQUIS.</th>
                    <th style={thBase}>DESTINO</th>
                    <th style={thR}>NR DOC</th>
                    <th style={thR}>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {/* durante o reload as linhas antigas permanecem (quem sinaliza a
                      carga é o botão Atualizar); o vazio só aparece parado. */}
                  {pendentes.length === 0 && carregandoPend ? (
                    <tr><td colSpan={10} style={{ ...tdBase, height: LINHA_H }}>&nbsp;</td></tr>
                  ) : pendentes.length === 0 ? (
                    <tr><td colSpan={10} style={{ ...tdBase, textAlign: 'center', color: '#94a3b8', padding: 16 }}>Nenhuma movimentação pendente.</td></tr>
                  ) : pendentes.map((p, i) => {
                    // cancelar vale para C e R; adicionar itens só para C, porque o
                    // endpoint de criação valida o DOC contra TIPO='C'.
                    const cancelavel = ['C', 'R'].includes(p.TIPO);
                    const editavel = p.TIPO === 'C';
                    const oQueE = TIPO_LABEL[p.TIPO] || `do tipo '${p.TIPO}'`;
                    const motivoAdd = `Só é possível acrescentar itens a documentos do Depto de Compras. Este é ${oQueE}.`;
                    const motivoCancel = `${oQueE.charAt(0).toUpperCase()}${oQueE.slice(1)} não pode ser cancelada por esta tela.`;
                    return (
                    <tr key={`${p.NRDOC}-${p.COD}-${i}`} style={{ borderTop: '1px solid #f1f5f9', opacity: cancelavel ? 1 : 0.65 }}>
                      <td style={tdBase}>{p.FIL}</td>
                      <td style={tdBase}>
                        <span title={TIPO_LABEL[p.TIPO] || p.TIPO}
                          style={{ padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 700, ...TIPO_ESTILO(p.TIPO) }}>
                          {TIPO_SIGLA[p.TIPO] || p.TIPO}
                        </span>
                      </td>
                      <td style={tdBase}>{p.COD}</td>
                      <td style={{ ...tdEll, maxWidth: 220 }} title={p.PRODUTO}>{p.PRODUTO}</td>
                      <td style={tdR}>{p.QTD}</td>
                      <td style={tdBase}>{p.DATA}</td>
                      <td style={{ ...tdEll, maxWidth: 110 }} title={p.REQUIS_COD ? `Operador ${p.REQUIS_COD}` : ''}>{p.REQUIS || '—'}</td>
                      <td style={{ ...tdEll, maxWidth: 150 }} title={p.DESTINO_NOME}>{p.DESTINO} - {p.DESTINO_NOME}</td>
                      <td style={{ ...tdR, fontWeight: 600 }}>{p.NRDOC}</td>
                      <td style={tdR}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <button onClick={() => iniciarAddDoc(p)} disabled={!editavel}
                            title={editavel ? 'Adicionar mais itens a este DOC' : motivoAdd}
                            style={{ padding: '3px 8px', border: '1px solid #0078d7', borderRadius: 6, background: '#fff', color: '#0078d7', cursor: editavel ? 'pointer' : 'not-allowed', fontSize: 11, ...(editavel ? {} : BTN_OFF) }}>
                            + Itens
                          </button>
                          <button onClick={() => cancelar(p, 'item')} disabled={!cancelavel || cancelandoKey === `${p.NRDOC}-${p.COD}`}
                            title={cancelavel ? 'Cancelar só este item' : motivoCancel}
                            style={{ padding: '3px 8px', border: '1px solid #dc2626', borderRadius: 6, background: '#fff', color: '#dc2626', cursor: cancelavel ? 'pointer' : 'not-allowed', fontSize: 11, ...(cancelavel ? {} : BTN_OFF) }}>
                            {cancelandoKey === `${p.NRDOC}-${p.COD}` ? '...' : 'Item'}
                          </button>
                          <button onClick={() => cancelar(p, 'doc')} disabled={!cancelavel || cancelandoKey === `doc-${p.NRDOC}`}
                            title={cancelavel ? 'Cancelar o documento inteiro' : motivoCancel}
                            style={{ padding: '3px 8px', border: 'none', borderRadius: 6, background: cancelavel ? '#dc2626' : '#cbd5e1', color: '#fff', cursor: cancelavel ? 'pointer' : 'not-allowed', fontSize: 11 }}>
                            {cancelandoKey === `doc-${p.NRDOC}` ? '...' : 'Doc'}
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
              <b>Item</b>: cancela apenas aquela linha. &nbsp;|&nbsp; <b>Doc</b>: cancela o documento (NR DOC) inteiro.
            </div>
          </div>
        )}

        {/* Mensagens (compartilhadas pelas abas) */}
        {erro && (
          <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: 6, marginTop: 12, fontSize: 13 }}>
            <i className="fas fa-triangle-exclamation" /> {erro}
          </div>
        )}
        {sucesso && (
          <div style={{ background: '#dcfce7', color: '#166534', padding: '8px 12px', borderRadius: 6, marginTop: 12, fontSize: 13 }}>
            <i className="fas fa-check-circle" /> {sucesso}
          </div>
        )}
      </div>
    </DraggableWindow>
  );
};

export default WindowTransferenciaMercadoria;
